import type { ChangeEvent } from 'react'

import {
  FvButton,
  FvLinkButton,
  TextField,
  useFvNavigate,
} from '@fv/client-components'

import { HasPermission } from '@/features/auth'
import { countLoads } from '@/features/quote-request/quoteRequestUtils'
import type { QuoteFormParams } from '@/features/quote-request/types'
import {
  useQuoteFormActions,
  useQuoteFormLoads,
  useQuoteFormMetadata,
} from '@/features/quote-request/useQuoteFormState'
import { useQuoteFormSubmission } from '@/features/quote-request/useQuoteFormSubmission'

import { DividedContent } from '../../components/DividedContent'
import { FooterBar } from '../../components/FooterBar'
import InfoBox from '../../components/InfoBox'
import {
  RadioItem,
  type RadioItemProps,
} from '../../components/inputs/RadioField'
import { Pinline } from '../../components/Pinline'

type LoadType = 'quote-only' | 'live-load' | 'customer-routed'
type Props = {
  isEditing: boolean
  mode?: QuoteFormParams['mode']
  quoteRequestId?: string
}

export const QuoteRequestFooter = ({
  isEditing,
  mode,
  quoteRequestId,
}: Props) => {
  const { bookCustomerRouted, editQuoteRequest, fetchQuotes, isBusy } =
    useQuoteFormSubmission(quoteRequestId, mode)

  const { setMetadata } = useQuoteFormActions()
  const loads = useQuoteFormLoads()
  const metadata = useQuoteFormMetadata()
  const navigate = useFvNavigate()

  function setLoadType(e: ChangeEvent<HTMLInputElement>) {
    const val = e.target.value as LoadType
    switch (val) {
      case 'customer-routed': {
        const loadsArray = [...loads.values()]
        const needsAlert =
          loadsArray.length > 1 || loadsArray.some(loads => loads.length > 1)
        if (needsAlert) {
          window.alert(
            'You cannot book a customer routed shipment with multiple trucks.',
          )
          return
        }

        setMetadata({ isCustomerRouted: true, isLiveLoad: false })
        break
      }
      case 'live-load':
        setMetadata({ isCustomerRouted: false, isLiveLoad: true })
        break
      case 'quote-only':
      default:
        setMetadata({ isCustomerRouted: false, isLiveLoad: false })
        break
    }
  }

  function setOrderNumber(e: ChangeEvent<HTMLInputElement>) {
    setMetadata({ orderNumber: e.target.value })
  }

  function submitQuoteRequest() {
    if (isEditing) {
      editQuoteRequest()
      return
    }
    if (metadata.isCustomerRouted) {
      bookCustomerRouted()
      return
    }
    fetchQuotes()
  }

  const primaryButtonLabel = isEditing
    ? 'Update quote request'
    : metadata.isCustomerRouted
      ? 'Create shipment'
      : 'Start getting quotes'

  const primaryButtonIcon = isEditing
    ? 'pen-alt'
    : metadata.isCustomerRouted
      ? 'truck-moving'
      : 'dollar-sign'

  if (isEditing && !countLoads(loads)) {
    return (
      <div className="footer-actions">
        <InfoBox icon="spinner">Loading...</InfoBox>
      </div>
    )
  }

  return (
    <FooterBar jc className="row-start-[-1] col-span-full flex-wrap">
      {isEditing && (
        <>
          <div className="edit-quote-request-reference flex flex-nowrap items-center fa-width-auto">
            <span className="truncate mb-0">
              Editing &gt; {metadata.requestIdentifier}
            </span>
            <FvLinkButton
              className="ml-6"
              icon="times"
              transform="down-.5"
              onClick={() => navigate(-1)}
            >
              <span>Cancel editing</span>
            </FvLinkButton>
          </div>
          <Pinline className="basis-full my-3 !border-fv-blue" dotted />
        </>
      )}
      <DividedContent theme="blue">
        <div className="flex gap-x-3 items-center first:pl-0">
          <LoadTypeRadio
            value="quote-only"
            checked={!metadata.isLiveLoad && !metadata.isCustomerRouted}
            onChange={setLoadType}
            label="Quote only"
            disabled={isBusy}
          />

          <LoadTypeRadio
            value="live-load"
            checked={metadata.isLiveLoad && !metadata.isCustomerRouted}
            onChange={setLoadType}
            label="Live load"
            disabled={isBusy}
          />

          {!isEditing && (
            <HasPermission name="load-booker">
              <LoadTypeRadio
                value="customer-routed"
                checked={metadata.isCustomerRouted}
                onChange={setLoadType}
                label="Customer routed (bill recipient / third party)"
                disabled={isBusy}
              />
            </HasPermission>
          )}
        </div>

        <div className="flex items-center last:pr-0">
          <label htmlFor="orderNumber" className="label mb-0 mr-2">
            Ref#:
          </label>
          <TextField
            className="form-control w-auto"
            name="orderNumber"
            onChange={setOrderNumber}
            placeholder="i.e. 45315576"
            value={metadata.orderNumber}
          />

          <FvButton
            fwd
            theme="primary"
            className="ml-4"
            onClick={submitQuoteRequest}
            type="button"
            loading={isBusy}
            icon={primaryButtonIcon}
          >
            {primaryButtonLabel}
          </FvButton>
        </div>
      </DividedContent>
    </FooterBar>
  )
}

type RadioProps = Omit<RadioItemProps, 'name' | 'id' | 'value'> & {
  value: LoadType
}
const LoadTypeRadio = (props: RadioProps) => {
  return (
    <RadioItem
      name="load-type"
      value={props.value}
      id={props.value}
      {...props}
    />
  )
}
